import { Button, IconButton, MenuItem, Select, TextField } from '@material-ui/core';
import ModuleContext from 'CMFW/context/ModuleContext';
import TextEditor from 'CMFW/input/TextEditor';
import PanelCard from 'components/common/PanelCard';
import { hasRefLevelScale, RefLevelChip } from 'components/selects/VocRefLevelSelect';
import PanelSearchModal from 'components/modal/PanelSearchModal';
import { IPanel } from 'models/PanelsModel';
import KlinesModel, { IKLineSelect } from 'models/KlinesModel';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { BaseModel } from 'models/BaseModel';
import * as api from './api';
import { htmlTitleToMd, mdTitleToHtml } from './titleFormat';
import { ConceptTitleText } from './ConceptTitleText';
import { IConcept, IKLine, LEVEL_NAMES, nodeState } from './types';

type IProps = {
  courseId: number;
  concept: IConcept;
  byCode: Map<string, IConcept>;
  klines: IKLine[];
  maxLevel: number | null;
  targetDialectId: number | null;
  onClose: () => void;
  onChanged: () => Promise<void>;
  onNavigate: (code: string) => void;
};

const requirementState = (req: IConcept | undefined, pending: boolean): string => {
  if (!req) return 'unknown';
  if (req.status === 'dropped') return 'dropped (ignored)';
  if (req.placedLesson !== null) return `placed L${req.placedLesson}`;
  return pending ? 'NOT studied yet' : 'satisfied';
};

export const DetailsPanel: React.FC<IProps> = (props) => {
  const { courseId, concept, byCode, klines, maxLevel } = props;
  const [busy, setBusy] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [reqSearch, setReqSearch] = useState('');
  const [reqNote, setReqNote] = useState('');
  const [reqPicked, setReqPicked] = useState<IConcept | null>(null);
  const [depSearch, setDepSearch] = useState('');
  const [depNote, setDepNote] = useState('');
  const [depPicked, setDepPicked] = useState<IConcept | null>(null);
  const [noteDraft, setNoteDraft] = useState(concept.note ?? '');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(mdTitleToHtml(concept.title));
  const [editingGroup, setEditingGroup] = useState(false);
  const [groupDraft, setGroupDraft] = useState(concept.groupCode ?? '');
  const [editingKline, setEditingKline] = useState(false);
  const [klineDraft, setKlineDraft] = useState(concept.klineId ?? 0);
  const [courseKlines, setCourseKlines] = useState<IKLineSelect[] | null>(null);
  const [editingConceptText, setEditingConceptText] = useState(false);
  const [conceptTextDraft, setConceptTextDraft] = useState(concept.conceptText ?? '');
  const [editingExamples, setEditingExamples] = useState(false);
  const [examplesDraft, setExamplesDraft] = useState('');

  React.useEffect(() => {
    setNoteDraft(concept.note ?? '');
    setTitleDraft(mdTitleToHtml(concept.title));
    setEditingTitle(false);
    setEditingGroup(false);
    setGroupDraft(concept.groupCode ?? '');
    setEditingKline(false);
    setKlineDraft(concept.klineId ?? 0);
    setEditingConceptText(false);
    setConceptTextDraft(concept.conceptText ?? '');
    setEditingExamples(false);
    setReqSearch('');
    setReqNote('');
    setReqPicked(null);
    setDepSearch('');
    setDepNote('');
    setDepPicked(null);
  }, [
    concept.code,
    concept.note,
    concept.title,
    concept.groupCode,
    concept.klineId,
    concept.conceptText,
    concept.examples,
  ]);

  const klineName = klines.find((k) => k.id === concept.klineId)?.name ?? '—';
  const state = nodeState(concept, maxLevel);

  const panelCardContext = useMemo(() => new BaseModel({ render: () => {}, courseId }), [courseId]);

  const dependents = useMemo(() => {
    const list: IConcept[] = [];
    byCode.forEach((c) => {
      if (c.requires.includes(concept.code)) list.push(c);
    });
    return list.sort((a, b) => a.code.localeCompare(b.code));
  }, [byCode, concept.code]);

  const suggestions = useMemo(() => {
    const term = reqSearch.trim().toLowerCase();
    if (term.length < 2) return [];
    const list: IConcept[] = [];
    byCode.forEach((c) => {
      if (c.code === concept.code || concept.requires.includes(c.code)) return;
      if (c.code.toLowerCase().includes(term) || c.title.toLowerCase().includes(term)) list.push(c);
    });
    return list.sort((a, b) => a.code.localeCompare(b.code)).slice(0, 8);
  }, [reqSearch, byCode, concept]);

  const depSuggestions = useMemo(() => {
    const term = depSearch.trim().toLowerCase();
    if (term.length < 2) return [];
    const list: IConcept[] = [];
    byCode.forEach((c) => {
      if (c.code === concept.code || c.requires.includes(concept.code)) return;
      if (c.code.toLowerCase().includes(term) || c.title.toLowerCase().includes(term)) list.push(c);
    });
    return list.sort((a, b) => a.code.localeCompare(b.code)).slice(0, 8);
  }, [depSearch, byCode, concept]);

  const run = async (action: () => Promise<boolean>) => {
    setBusy(true);
    const ok = await action();
    if (ok) await props.onChanged();
    setBusy(false);
  };

  const gateBlocked = maxLevel !== null && concept.level > maxLevel && concept.placedLesson === null;
  const examples = concept.examples
    ? concept.examples
        .split(/\s*(?:·|\r?\n)\s*/)
        .map((example) => example.trim())
        .filter(Boolean)
    : [];
  // Edited and stored one example per line (imported trees may also use « · »; both display the same).
  const examplesFromDraft = examplesDraft
    .split(/\r?\n/)
    .map((example) => example.trim())
    .filter(Boolean)
    .join('\n');

  return (
    <Wrapper>
      <Header>
        <code>{concept.code}</code>
        <StateChip $state={state}>{state}</StateChip>
        {props.targetDialectId !== null && hasRefLevelScale(props.targetDialectId) && (
          <RefLevelChip
            dialectId={props.targetDialectId}
            value={concept.refLevel ?? 0}
            onSelect={(level) => run(() => api.updateConceptRefLevel(courseId, concept.code, level))}
          />
        )}
        <CloseButton onClick={props.onClose}>✕</CloseButton>
      </Header>
      {!editingTitle ? (
        <Title>
          <ConceptTitleText text={concept.title} />
          <EditIcon title="Edit title" onClick={() => setEditingTitle(true)}>
            ✎
          </EditIcon>
        </Title>
      ) : (
        <>
          <EditorBox>
            <ModuleContext.Provider value={panelCardContext}>
              <TextEditor
                key={concept.code}
                keyName={''}
                config={{ toolBarInline: [['lang_target']] }}
                text={titleDraft}
                onChangeText={setTitleDraft}
              />
            </ModuleContext.Provider>
          </EditorBox>
          <NoteActions>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={busy || htmlTitleToMd(titleDraft) === '' || htmlTitleToMd(titleDraft) === concept.title}
              onClick={() => run(() => api.updateConceptTitle(courseId, concept.code, htmlTitleToMd(titleDraft)))}
            >
              Save title
            </Button>
            <Button size="small" disabled={busy} onClick={() => setEditingTitle(false)}>
              Cancel
            </Button>
          </NoteActions>
        </>
      )}
      {concept.transcription && <Transcription>{concept.transcription}</Transcription>}
      {!editingKline ? (
        <Meta>
          KLine: <ConceptTitleText text={klineName} /> · pos {concept.klinePos}
          {concept.panelId ? ` · panel #${concept.panelId}` : ' · no panel yet'}
          {concept.placedLesson !== null ? ` · placed in L${concept.placedLesson}` : ''}
          <EditIcon
            title="Move to another KLine"
            onClick={async () => {
              if (courseKlines === null) setCourseKlines(await new KlinesModel(courseId).get());
              setEditingKline(true);
            }}
          >
            ✎
          </EditIcon>
        </Meta>
      ) : (
        <>
          <Select
            fullWidth
            value={courseKlines === null ? '' : klineDraft}
            disabled={busy || courseKlines === null}
            onChange={(e) => setKlineDraft(Number(e.target.value))}
          >
            <MenuItem value={0}>— (no KLine)</MenuItem>
            {(courseKlines ?? []).map((k) => (
              <MenuItem key={k.id} value={k.id}>
                {k.title}
              </MenuItem>
            ))}
          </Select>
          <NoteActions>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={busy || klineDraft === (concept.klineId ?? 0)}
              onClick={() => run(() => api.updateConceptKline(courseId, concept.code, klineDraft))}
            >
              Save KLine
            </Button>
            <Button size="small" disabled={busy} onClick={() => setEditingKline(false)}>
              Cancel
            </Button>
          </NoteActions>
        </>
      )}
      <SectionTitle>
        Concept
        {!editingConceptText && (
          <EditIcon title="Edit concept text" onClick={() => setEditingConceptText(true)}>
            ✎
          </EditIcon>
        )}
      </SectionTitle>
      {!editingConceptText ? (
        concept.conceptText ? (
          <ConceptBox>
            <ConceptTitleText text={concept.conceptText} />
          </ConceptBox>
        ) : (
          <Meta>—</Meta>
        )
      ) : (
        <>
          <TextField
            fullWidth
            multiline
            placeholder="What the form does, 1–3 sentences (*target words* between asterisks)…"
            value={conceptTextDraft}
            onChange={(e) => setConceptTextDraft(e.target.value)}
          />
          <NoteActions>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={busy || conceptTextDraft.trim() === (concept.conceptText ?? '')}
              onClick={() => run(() => api.updateConceptText(courseId, concept.code, conceptTextDraft.trim()))}
            >
              Save concept
            </Button>
            <Button
              size="small"
              disabled={busy}
              onClick={() => {
                setConceptTextDraft(concept.conceptText ?? '');
                setEditingConceptText(false);
              }}
            >
              Cancel
            </Button>
          </NoteActions>
        </>
      )}

      <SectionTitle>
        Examples
        {!editingExamples && (
          <EditIcon
            title="Edit examples"
            onClick={() => {
              setExamplesDraft(examples.join('\n'));
              setEditingExamples(true);
            }}
          >
            ✎
          </EditIcon>
        )}
      </SectionTitle>
      {!editingExamples ? (
        examples.length > 0 ? (
          <ExamplesList>
            {examples.map((example, index) => (
              <ExampleRow key={`${concept.code}-example-${index}`}>
                <ConceptTitleText text={example} />
              </ExampleRow>
            ))}
          </ExamplesList>
        ) : (
          <Meta>—</Meta>
        )
      ) : (
        <>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="One example per line, with its translation — e.g. *Ela está cansada.* (She is tired.)"
            value={examplesDraft}
            onChange={(e) => setExamplesDraft(e.target.value)}
          />
          <NoteActions>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={busy || examplesFromDraft === examples.join('\n')}
              onClick={() => run(() => api.updateConceptExamples(courseId, concept.code, examplesFromDraft))}
            >
              Save examples
            </Button>
            <Button size="small" disabled={busy} onClick={() => setEditingExamples(false)}>
              Cancel
            </Button>
          </NoteActions>
        </>
      )}

      <SectionTitle>Group (atomic siblings — placed in the SAME block)</SectionTitle>
      {!editingGroup ? (
        <Meta>
          {concept.groupCode ?? '—'}
          <EditIcon title="Edit group code" onClick={() => setEditingGroup(true)}>
            ✎
          </EditIcon>
        </Meta>
      ) : (
        <>
          <TextField
            fullWidth
            size="small"
            placeholder="Group code (empty clears it) — e.g. G-kl001-iyor"
            value={groupDraft}
            onChange={(e) => setGroupDraft(e.target.value)}
          />
          <NoteActions>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={busy || groupDraft.trim() === (concept.groupCode ?? '')}
              onClick={() => run(() => api.updateConceptGroup(courseId, concept.code, groupDraft.trim()))}
            >
              Save group
            </Button>
            <Button size="small" disabled={busy} onClick={() => setEditingGroup(false)}>
              Cancel
            </Button>
          </NoteActions>
        </>
      )}

      <SectionTitle>Note</SectionTitle>
      <TextField
        fullWidth
        multiline
        placeholder="Editorial note for this concept…"
        value={noteDraft}
        onChange={(e) => setNoteDraft(e.target.value)}
      />
      {noteDraft !== (concept.note ?? '') && (
        <NoteActions>
          <Button
            size="small"
            color="primary"
            variant="contained"
            disabled={busy}
            onClick={() => run(() => api.updateConceptNote(courseId, concept.code, noteDraft.trim()))}
          >
            Save note
          </Button>
          <Button size="small" disabled={busy} onClick={() => setNoteDraft(concept.note ?? '')}>
            Discard
          </Button>
        </NoteActions>
      )}

      {concept.panelId !== null ? (
        <>
          <SectionTitle>
            Panel{concept.placedLesson !== null ? ` (placed in L${concept.placedLesson})` : ' (linked, not placed)'}
            <SmallAction onClick={() => run(() => api.unlinkConceptPanel(courseId, concept.code))}>unlink</SmallAction>
          </SectionTitle>
          <ModuleContext.Provider value={panelCardContext}>
            <PanelCard id={concept.panelId} />
          </ModuleContext.Provider>
        </>
      ) : (
        <>
          <SectionTitle>Panel</SectionTitle>
          <Button size="small" variant="outlined" disabled={busy} onClick={() => setLinkModalOpen(true)}>
            Link to panel…
          </Button>
        </>
      )}
      {linkModalOpen && (
        <ModuleContext.Provider value={panelCardContext}>
          <PanelSearchModal
            courseId={courseId}
            onClose={() => setLinkModalOpen(false)}
            onChoosePanel={(panel: IPanel) => {
              setLinkModalOpen(false);
              run(() => api.linkConceptPanel(courseId, concept.code, panel.id));
            }}
          />
        </ModuleContext.Provider>
      )}

      <SectionTitle>Why {state === 'blocked' || state === 'gated' ? 'is it locked?' : 'status'}</SectionTitle>
      {gateBlocked && (
        <GateRow>
          Level gate: needs level {LEVEL_NAMES[concept.level] ?? concept.level}, browsing at{' '}
          {LEVEL_NAMES[maxLevel as number]}.
        </GateRow>
      )}
      {concept.requires.length === 0 && <Empty>No requirements — a root concept.</Empty>}
      {concept.requires.map((code) => {
        const req = byCode.get(code);
        const pending = concept.pendingRequires.includes(code);
        return (
          <ReqRow key={code} $pending={pending}>
            <ReqInfo onClick={() => props.onNavigate(code)}>
              <code>{code}</code> <ConceptTitleText text={req?.title ?? ''} />
              <ReqState $pending={pending}>{requirementState(req, pending)}</ReqState>
              {concept.requireNotes?.[code] && <EdgeNote>“{concept.requireNotes[code]}”</EdgeNote>}
            </ReqInfo>
            <IconButton
              size="small"
              title="Remove requirement"
              disabled={busy}
              onClick={() => run(() => api.removeRequirement(courseId, concept.code, code))}
            >
              ✕
            </IconButton>
          </ReqRow>
        );
      })}

      <SectionTitle>Add requirement (must come before this)</SectionTitle>
      {reqPicked ? (
        <PickedBox>
          <PickedRow>
            <span>
              <code>{reqPicked.code}</code> <ConceptTitleText text={reqPicked.title} /> must come <b>before</b>{' '}
              <code>{concept.code}</code>
            </span>
            <ClearPick onClick={() => setReqPicked(null)}>✕</ClearPick>
          </PickedRow>
          <TextField
            fullWidth
            size="small"
            placeholder="Why? (optional note, saved on the edge)"
            value={reqNote}
            onChange={(e) => setReqNote(e.target.value)}
          />
          <NoteActions>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const ok = await api.addRequirement(courseId, concept.code, reqPicked.code, reqNote || undefined);
                  if (ok) {
                    setReqPicked(null);
                    setReqSearch('');
                    setReqNote('');
                  }
                  return ok;
                })
              }
            >
              Add requirement
            </Button>
            <Button size="small" disabled={busy} onClick={() => setReqPicked(null)}>
              Cancel
            </Button>
          </NoteActions>
        </PickedBox>
      ) : (
        <>
          <TextField
            fullWidth
            size="small"
            placeholder="Search concept by code or title…"
            value={reqSearch}
            onChange={(e) => setReqSearch(e.target.value)}
          />
          {suggestions.length > 0 && (
            <Suggestions>
              {suggestions.map((s) => (
                <SuggestionRow key={s.code} onClick={() => setReqPicked(s)}>
                  <code>{s.code}</code> <ConceptTitleText text={s.title} />
                </SuggestionRow>
              ))}
            </Suggestions>
          )}
        </>
      )}

      <SectionTitle>Make this a requirement of…</SectionTitle>
      {depPicked ? (
        <PickedBox>
          <PickedRow>
            <span>
              <code>{concept.code}</code> must come <b>before</b> <code>{depPicked.code}</code>{' '}
              <ConceptTitleText text={depPicked.title} />
            </span>
            <ClearPick onClick={() => setDepPicked(null)}>✕</ClearPick>
          </PickedRow>
          <TextField
            fullWidth
            size="small"
            placeholder="Why? (optional note, saved on the edge)"
            value={depNote}
            onChange={(e) => setDepNote(e.target.value)}
          />
          <NoteActions>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const ok = await api.addRequirement(courseId, depPicked.code, concept.code, depNote || undefined);
                  if (ok) {
                    setDepPicked(null);
                    setDepSearch('');
                    setDepNote('');
                  }
                  return ok;
                })
              }
            >
              Add requirement
            </Button>
            <Button size="small" disabled={busy} onClick={() => setDepPicked(null)}>
              Cancel
            </Button>
          </NoteActions>
        </PickedBox>
      ) : (
        <>
          <TextField
            fullWidth
            size="small"
            placeholder="Search the concept this one must come before…"
            value={depSearch}
            onChange={(e) => setDepSearch(e.target.value)}
          />
          {depSuggestions.length > 0 && (
            <Suggestions>
              {depSuggestions.map((s) => (
                <SuggestionRow key={s.code} onClick={() => setDepPicked(s)}>
                  <code>{s.code}</code> <ConceptTitleText text={s.title} />
                </SuggestionRow>
              ))}
            </Suggestions>
          )}
        </>
      )}

      <SectionTitle>Level gate</SectionTitle>
      <Select
        value={concept.level}
        disabled={busy}
        onChange={(e) => run(() => api.setConceptLevel(courseId, concept.code, Number(e.target.value)))}
      >
        {[1, 2, 3, 4].map((level) => (
          <MenuItem key={level} value={level}>
            {LEVEL_NAMES[level]}
          </MenuItem>
        ))}
      </Select>

      <SectionTitle>Unlocks ({dependents.length})</SectionTitle>
      {dependents.length === 0 && <Empty>Nothing depends on this concept.</Empty>}
      <DependentsList>
        {dependents.map((d) => (
          <DependentRow key={d.code} onClick={() => props.onNavigate(d.code)}>
            <code>{d.code}</code> <ConceptTitleText text={d.title} />
          </DependentRow>
        ))}
      </DependentsList>

      <Actions>
        <DeleteButton
          title={
            concept.placedLesson !== null
              ? 'Placed concepts cannot be deleted'
              : 'Remove the concept and its edges permanently'
          }
          disabled={busy || concept.placedLesson !== null || concept.status === 'built'}
          onClick={() => {
            if (window.confirm(`Delete ${concept.code} permanently? Its requirement edges are removed too. This cannot be undone (use "Don't teach" for editorial decisions).`)) {
              run(async () => {
                const ok = await api.deleteConcept(courseId, concept.code);
                if (ok) props.onClose();
                return ok;
              });
            }
          }}
        >
          Delete
        </DeleteButton>
        {concept.status !== 'dropped' ? (
          <Button
            variant="outlined"
            size="small"
            disabled={busy || concept.placedLesson !== null}
            onClick={() => run(() => api.dropConcept(courseId, concept.code))}
          >
            Don't teach in this course
          </Button>
        ) : (
          <Button
            variant="outlined"
            size="small"
            disabled={busy}
            onClick={() => run(() => api.restoreConcept(courseId, concept.code))}
          >
            Restore concept
          </Button>
        )}
      </Actions>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 410px;
  min-width: 410px;
  height: 100%;
  box-sizing: border-box;
  border-left: 1px solid #ddd;
  padding: 14px 18px;
  overflow-y: auto;
  background: #fafafa;
  font-size: 15px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  code {
    font-weight: bold;
    font-size: 17px;
  }
`;

const CloseButton = styled.span`
  margin-left: auto;
  cursor: pointer;
  color: #888;
  &:hover {
    color: #000;
  }
`;

const StateChip = styled.span<{ $state: string }>`
  padding: 2px 10px;
  border-radius: 11px;
  font-size: 13px;
  color: #fff;
  background: ${(p) =>
    p.$state === 'placed'
      ? '#2e7d32'
      : p.$state === 'eligible'
      ? '#1565c0'
      : p.$state === 'blocked'
      ? '#e65100'
      : p.$state === 'gated'
      ? '#6a1b9a'
      : '#757575'};
`;

const Title = styled.div`
  font-size: 17px;
  font-weight: 500;
  margin: 10px 0 5px;
  line-height: 1.35;
`;

const Meta = styled.div`
  color: #666;
  margin-bottom: 6px;
`;

const Transcription = styled.div`
  color: #777;
  font-style: italic;
  font-size: 14.5px;
  margin: -2px 0 4px;
`;

const ConceptBox = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-left: 3px solid #2087ff;
  border-radius: 4px;
  padding: 8px 10px;
  line-height: 1.45;
  margin-bottom: 6px;
`;

const ExamplesList = styled.ul`
  margin: 0 0 6px;
  padding: 0;
  list-style: none;
`;

const ExampleRow = styled.li`
  position: relative;
  margin-bottom: 5px;
  padding: 7px 10px 7px 22px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  line-height: 1.4;

  &::before {
    content: '•';
    position: absolute;
    left: 10px;
    color: #2087ff;
    font-weight: 700;
  }
`;

const DeleteButton = styled(Button).attrs({ variant: 'outlined', size: 'small' })`
  && {
    color: #c62828;
    border-color: #c62828;
  }
  &&:disabled {
    color: rgba(0, 0, 0, 0.26);
    border-color: rgba(0, 0, 0, 0.12);
  }
`;

const SmallAction = styled.span`
  margin-left: 8px;
  font-weight: normal;
  font-size: 11px;
  color: #c62828;
  cursor: pointer;
  text-transform: none;
  &:hover {
    text-decoration: underline;
  }
`;

const EditIcon = styled.span`
  margin-left: 8px;
  color: #999;
  cursor: pointer;
  font-size: 15px;
  &:hover {
    color: #1565c0;
  }
`;

const EditorBox = styled.div`
  height: 84px;
  margin-bottom: 8px;
`;

const NoteActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 6px;
`;

const EdgeNote = styled.div`
  font-size: 13px;
  color: #8d6e63;
  font-style: italic;
  margin-top: 2px;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  margin: 16px 0 7px;
  text-transform: uppercase;
  font-size: 12.5px;
  color: #555;
`;

const GateRow = styled.div`
  background: #f3e5f5;
  border-left: 3px solid #6a1b9a;
  padding: 6px 8px;
  margin-bottom: 6px;
`;

const ReqRow = styled.div<{ $pending: boolean }>`
  display: flex;
  align-items: center;
  border: 1px solid ${(p) => (p.$pending ? '#ffb74d' : '#e0e0e0')};
  background: ${(p) => (p.$pending ? '#fff3e0' : '#fff')};
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 6px;
`;

const ReqInfo = styled.div`
  flex: 1;
  cursor: pointer;
  code {
    font-weight: 600;
  }
  &:hover {
    text-decoration: underline;
  }
`;

const ReqState = styled.div<{ $pending: boolean }>`
  font-size: 13px;
  color: ${(p) => (p.$pending ? '#e65100' : '#2e7d32')};
`;

const PickedBox = styled.div`
  border: 1px solid #90caf9;
  background: #e3f2fd;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 6px;
`;

const PickedRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 8px;
  code {
    font-weight: 700;
  }
`;

const ClearPick = styled.span`
  margin-left: auto;
  cursor: pointer;
  color: #888;
  &:hover {
    color: #000;
  }
`;

const Suggestions = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 4px;
  background: #fff;
`;

const SuggestionRow = styled.div`
  padding: 6px 10px;
  cursor: pointer;
  &:hover {
    background: #e3f2fd;
  }
  code {
    font-weight: 600;
  }
`;

const DependentsList = styled.div`
  max-height: 180px;
  overflow-y: auto;
`;

const DependentRow = styled.div`
  padding: 4px 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  code {
    font-weight: 600;
  }
`;

const Empty = styled.div`
  color: #999;
  font-style: italic;
`;

const Actions = styled.div`
  margin: 16px 0;
  display: flex;
  gap: 8px;
`;
